import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ListSkeleton } from '@/components/PageSkeleton';
import { ErrorState } from '@/components/kit';
import { STATUS_PATH, latestUpdate, loadPublicStatus, metaOf, overallOf } from '@/shared/status';
import { useSeo } from '@/shared/seo';
import { formatRelativeTime } from '@/utils/date';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { P, around } from '@/shared/locales/pages/keys';

export const route = { path: '/status', layout: 'public', access: 'public' };

const REFRESH_MS = 60_000;

function Overall({ services }) {
  const { t } = useT();
  const overall = overallOf(services);
  const meta = metaOf(overall);
  const updated = latestUpdate(services);
  return (
    <div className={cn('flex items-start gap-4 rounded-lg border px-5 py-5 text-start', meta.badge)}>
      <ApperIcon name={meta.icon} size={28} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-lg font-medium">{t(meta.headline)}</p>
        <p className="mt-1 text-sm text-foreground/80">{t(meta.blurb)}</p>
        {updated && <p className="mt-2 text-xs text-foreground/60">{t(P.updated, { time: formatRelativeTime(updated) })}</p>}
      </div>
    </div>
  );
}

function ServiceRow({ service }) {
  const { t } = useT();
  const meta = metaOf(service.status_c);
  return (
    <li className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="font-medium">{service.Name}</p>
        {service.message_c && (
          <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{service.message_c}</p>
        )}
      </div>
      <span className={cn('inline-flex shrink-0 items-center gap-2 text-sm font-medium', meta.text)}>
        <span className={cn('h-2.5 w-2.5 rounded-full', meta.dot, service.status_c !== 'Up' && 'animate-pulse')} />
        {t(meta.label)}
      </span>
    </li>
  );
}

export default function Status() {
  const { t } = useT();
  const { data, loading, error, run } = useFetch(loadPublicStatus, []);

  useSeo({
    title: 'Service status',
    description: 'Live status of Ghost Cable streams, the program guide and apps. See right away if something is having issues.',
    path: STATUS_PATH,
  });

  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    const timer = setInterval(() => runRef.current?.(), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const services = data ?? [];
  const firstLoad = loading && data === undefined;
  const [isPre, isPost] = around(t(P.isThe), 'signal');

  return (
    <div className="pb-24">
      <section className={cn(WRAP, 'pt-16 md:pt-24')}>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
            {t(P.back)}
          </Link>
        </Button>

        <div className="mx-auto mt-10 max-w-2xl">
          <div className="text-center">
            <p className={cn(LABEL, 'flex items-center justify-center gap-2')}>
              <ApperIcon name="Activity" size={12} />
              {t('Service status')}
            </p>
            <h1 className="mt-4 text-[clamp(36px,6vw,64px)] font-light leading-[1.05]">
              {isPre}
              <span className={GLOW}>{t(P.signal)}</span>
              {isPost}
            </h1>
            <p className="mt-4 text-[17px] leading-8 text-muted-foreground">
              {t(P.stateIntro)}
            </p>
          </div>

          <div className="mt-10" aria-live="polite" aria-busy={loading}>
            {firstLoad ? (
              <ListSkeleton rows={4} />
            ) : error ? (
              <ErrorState message={t(P.cantLoad)} onRetry={run} />
            ) : services.length === 0 ? (
              <div className="rounded-lg border border-border bg-card px-5 py-8 text-center">
                <ApperIcon name="Radio" size={24} className="mx-auto text-primary" />
                <p className="mt-3 font-medium">{t(P.noServices)}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(P.trouble)}{' '}
                  <Link to="/#contact" className="text-primary hover:underline">
                    {t(P.sendMsg)}
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <Overall services={services} />
                <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                  {services.map((s) => (
                    <ServiceRow key={s.Id} service={s} />
                  ))}
                </ul>
                <p className="text-center text-sm text-muted-foreground">
                  {t(P.stillTrouble)}{' '}
                  <Link to="/#contact" className="text-primary hover:underline">
                    {t(P.contactSupport)}
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
